import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set the auth header synchronously at module load so admin pages that fetch on
// mount (incl. hard-reload / direct URL) never race the AuthProvider effect.
const _initialToken = localStorage.getItem('reach_token');
if (_initialToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${_initialToken}`;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(_initialToken);

  const logout = useCallback(() => {
    localStorage.removeItem('reach_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  // Expired/invalid session: clear it and send team members back to login instead of showing empty pages.
  useEffect(() => {
    const id = axios.interceptors.response.use((r) => r, (err) => {
      const d = err.response?.data?.detail;
      if (err.response?.status === 401 && (d === 'Token expired' || d === 'Invalid token') && localStorage.getItem('reach_token')) {
        logout();
        if (window.location.pathname.startsWith('/admin')) window.location.assign(`/login?expired=1&next=${encodeURIComponent(window.location.pathname)}`);
      }
      return Promise.reject(err);
    });
    return () => axios.interceptors.response.eject(id);
  }, [logout]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('reach_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, company) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, company });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('reach_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const acceptInvite = async (inviteToken, password, name) => {
    const response = await axios.post(`${API}/public/team/accept-invite`, { token: inviteToken, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('reach_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // Supplier magic-link: store a server-minted JWT and load the session.
  const loginWithToken = async (access_token, userData = null) => {
    localStorage.removeItem('active_org_id'); // suppliers are locked to their own scope
    localStorage.setItem('reach_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    if (userData) setUser(userData);
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, token, acceptInvite, loginWithToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
